import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Grid,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Settings as SettingsIcon,
  Print as PrintIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { useTheme } from '../theme/ThemeContext';
import { settingsAPI, apiUtils } from '../services/api';

const Settings = () => {
  const { themeMode, setTheme, toggleTheme, isDark } = useTheme();

  // Business settings state (loaded from database)
  const [thermalWidth, setThermalWidth] = React.useState(58);
  const [orderSlipEnabled, setOrderSlipEnabled] = React.useState(false);
  const [serviceChargeEnabled, setServiceChargeEnabled] = React.useState(false);
  const [serviceChargePercentage, setServiceChargePercentage] = React.useState(10.00);
  const [serviceChargeInclusive, setServiceChargeInclusive] = React.useState(false);
  
  // Printer settings state (loaded from localStorage - device specific)
  const [printerType, setPrinterType] = React.useState('browser');
  const [qzTrayEnabled, setQzTrayEnabled] = React.useState(false);
  const [qzTrayPrinterName, setQzTrayPrinterName] = React.useState('');
  const [rawbtEnabled, setRawbtEnabled] = React.useState(false);

  // Helper functions for localStorage operations
  const loadPrinterSettings = () => {
    try {
      const savedPrinterType = localStorage.getItem('printer-type') || 'browser';
      const savedQzTrayEnabled = localStorage.getItem('qz-tray-enabled') === 'true';
      const savedQzTrayPrinterName = localStorage.getItem('qz-tray-printer-name') || '';
      const savedRawbtEnabled = localStorage.getItem('rawbt-enabled') === 'true';

      setPrinterType(savedPrinterType);
      setQzTrayEnabled(savedQzTrayEnabled);
      setQzTrayPrinterName(savedQzTrayPrinterName);
      setRawbtEnabled(savedRawbtEnabled);
    } catch (error) {
      console.error('Error loading printer settings from localStorage:', error);
    }
  };

  const savePrinterSetting = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error('Error saving printer setting to localStorage:', error);
    }
  };
  
  // Loading and error states
  const [settingsLoading, setSettingsLoading] = React.useState(false);
  const [settingsSaving, setSettingsSaving] = React.useState(false);
  const [settingsError, setSettingsError] = React.useState(null);
  const [settingsSuccess, setSettingsSuccess] = React.useState(false);

  // Feedback form state
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [message, setMessage] = React.useState('');

  // Load settings from database on component mount
  React.useEffect(() => {
    const loadSettings = async () => {
      setSettingsLoading(true);
      setSettingsError(null);
      
      try {
        // Load printer settings from localStorage (device-specific)
        loadPrinterSettings();
        
        // Load business settings from database
        const response = await settingsAPI.fetch();
        const settings = apiUtils.formatResponse(response)
        
        if (settings) {          
          // Update business settings from database
          setServiceChargeEnabled(Boolean(settings.service_charge_enabled));
          setServiceChargePercentage(settings.service_charge_percentage || 10.00);
          setServiceChargeInclusive(Boolean(settings.service_charge_inclusive));
          setThermalWidth(settings.thermal_printer_width || 58);
          setOrderSlipEnabled(Boolean(settings.order_slip_enabled));
          
          // Update theme if it's different from current
          if (settings.theme_mode && settings.theme_mode !== themeMode) {
            setTheme(settings.theme_mode);
          }
          
          // Remove old localStorage values as they're now handled separately
          localStorage.removeItem('thermal-printer-width');
          localStorage.removeItem('order-slip-enabled');
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        setSettingsError('Failed to load settings. Using default values.');
        // Load printer settings even if database fails
        loadPrinterSettings();
        // Keep default values for business settings if loading fails
        setServiceChargeEnabled(false);
        setServiceChargePercentage(10.00);
        setServiceChargeInclusive(false);
        setThermalWidth(58);
        setOrderSlipEnabled(false);
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSettings();
  }, [themeMode, setTheme]);

  // Auto-save settings to database with debouncing
  const saveSettingsToDatabase = React.useCallback(async (settingsData) => {
    setSettingsSaving(true);
    setSettingsError(null);
    
    try {
      await settingsAPI.update(settingsData);
      setSettingsSuccess(true);
      // Clear success message after 2 seconds
      setTimeout(() => setSettingsSuccess(false), 2000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSettingsError('Failed to save settings. Please try again.');
    } finally {
      setSettingsSaving(false);
    }
  }, []);

  // Debounced save function
  const debouncedSave = React.useCallback(
    React.useMemo(() => {
      let timeoutId;
      return (settingsData) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          saveSettingsToDatabase(settingsData);
        }, 500); // 500ms delay
      };
    }, [saveSettingsToDatabase]),
    [saveSettingsToDatabase]
  );

  // Handle theme change
  const handleThemeChange = (event) => {
    const newTheme = event.target.value;
    setTheme(newTheme);
    debouncedSave({ theme_mode: newTheme });
  };

  // Handle thermal width change
  const handleThermalWidthChange = (event) => {
    const newWidth = parseInt(event.target.value);
    setThermalWidth(newWidth);
    debouncedSave({ thermal_printer_width: newWidth });
  };

  // Handle order slip toggle
  const handleOrderSlipToggle = (event) => {
    const enabled = event.target.checked;
    setOrderSlipEnabled(enabled);
    debouncedSave({ order_slip_enabled: enabled });
  };

  // Handle theme toggle (quick toggle button)
  const handleThemeToggle = () => {
    const newTheme = themeMode === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    debouncedSave({ theme_mode: newTheme });
  };

  // Handle service charge enabled toggle
  const handleServiceChargeEnabledChange = (event) => {
    const enabled = event.target.checked;
    setServiceChargeEnabled(enabled);
    debouncedSave({ 
      service_charge_enabled: enabled,
      service_charge_percentage: serviceChargePercentage,
      service_charge_inclusive: serviceChargeInclusive
    });
  };

  // Handle service charge percentage change
  const handleServiceChargePercentageChange = (event) => {
    const percentage = parseFloat(event.target.value) || 0;
    setServiceChargePercentage(percentage);
    debouncedSave({ 
      service_charge_enabled: serviceChargeEnabled,
      service_charge_percentage: percentage,
      service_charge_inclusive: serviceChargeInclusive
    });
  };

  // Handle service charge inclusive toggle
  const handleServiceChargeInclusiveChange = (event) => {
    const inclusive = event.target.checked;
    setServiceChargeInclusive(inclusive);
    debouncedSave({ 
      service_charge_enabled: serviceChargeEnabled,
      service_charge_percentage: serviceChargePercentage,
      service_charge_inclusive: inclusive
    });
  };

  // Handle printer type change
  const handlePrinterTypeChange = (event) => {
    const newType = event.target.value;
    setPrinterType(newType);
    savePrinterSetting('printer-type', newType);
    
    // Reset other printer settings when changing type
    if (newType !== 'qz_tray') {
      setQzTrayEnabled(false);
      savePrinterSetting('qz-tray-enabled', 'false');
    }
    if (newType !== 'rawbt') {
      setRawbtEnabled(false);
      savePrinterSetting('rawbt-enabled', 'false');
    }
  };

  // Handle QZ Tray settings
  const handleQzTrayEnabledChange = (event) => {
    const enabled = event.target.checked;
    setQzTrayEnabled(enabled);
    savePrinterSetting('qz-tray-enabled', enabled.toString());
    
    if (enabled) {
      setPrinterType('qz_tray');
      savePrinterSetting('printer-type', 'qz_tray');
    } else {
      setPrinterType('browser');
      savePrinterSetting('printer-type', 'browser');
    }
  };

  const handleQzTrayPrinterNameChange = (event) => {
    const name = event.target.value;
    setQzTrayPrinterName(name);
    savePrinterSetting('qz-tray-printer-name', name);
  };

  // Handle RawBT settings
  const handleRawbtEnabledChange = (event) => {
    const enabled = event.target.checked;
    setRawbtEnabled(enabled);
    savePrinterSetting('rawbt-enabled', enabled.toString());
    
    if (enabled) {
      setPrinterType('rawbt');
      savePrinterSetting('printer-type', 'rawbt');
    } else {
      setPrinterType('browser');
      savePrinterSetting('printer-type', 'browser');
    }
  };

  const handleSendFeedback = () => {
    if (!name || !email || !message) {
      alert('Please fill in all required fields.');
      return;
    }
    const subject = encodeURIComponent(`App Feedback from ${name}`);
    const body = encodeURIComponent(`Name: ${name}\nEmail: ${email}\n\nMessage:\n${message}`);
    const mailtoLink = `mailto:jom.romero@gmail.com?subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;
  };

  const themeColors = {
    light: {
      primary: '#00A16A',
      secondary: '#1976D2',
      background: '#F7F8FA',
      paper: '#EDEFF2',
      text: '#1E1E1E',
    },
    dark: {
      primary: '#00B97D',
      secondary: '#2196F3',
      background: '#1C1F26',
      paper: '#262A33',
      text: '#FFFFFF',
    },
  };

  const ColorSwatch = ({ color, label }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Box
        sx={{
          width: 24,
          height: 24,
          backgroundColor: color,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}
      />
      <Typography variant="body2">{label}</Typography>
    </Box>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        <Typography variant="h4" component="h1">
          Settings
        </Typography>
      </Box>

      <Paper 
        sx={{ 
          height: 'calc(100vh - 140px)', 
          width: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'auto',
          p: 3,
        }}
      >
        {/* Global Settings Status */}
        {settingsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {settingsError}
          </Alert>
        )}

        {settingsSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Settings saved successfully!
          </Alert>
        )}

        {settingsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, mb: 2 }}>
            <CircularProgress sx={{ mr: 2 }} />
            <Typography>Loading settings...</Typography>
          </Box>
        )}

        <Grid container spacing={3}>
          {/* Theme Settings */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PaletteIcon color="primary" />
                  <Typography variant="h6">Theme Settings</Typography>
                </Box>
                
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <FormLabel component="legend" sx={{ mb: 2 }}>
                    Choose your preferred theme
                  </FormLabel>
                  <RadioGroup
                    value={themeMode}
                    onChange={handleThemeChange}
                    sx={{ gap: 2 }}
                  >
                    <Paper 
                      sx={{ 
                        p: 2, 
                        border: themeMode === 'light' ? 2 : 1,
                        borderColor: themeMode === 'light' ? 'primary.main' : 'divider',
                        bgcolor: themeMode === 'light' ? 'action.selected' : 'inherit',
                      }}
                    >
                      <FormControlLabel
                        value="light"
                        control={<Radio disabled={settingsLoading || settingsSaving} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LightModeIcon />
                            <Typography variant="subtitle1">Light Theme</Typography>
                          </Box>
                        }
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Clean and bright interface perfect for well-lit environments
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <ColorSwatch color={themeColors.light.primary} label="Primary" />
                        <ColorSwatch color={themeColors.light.secondary} label="Secondary" />
                        <ColorSwatch color={themeColors.light.background} label="Background" />
                        <ColorSwatch color={themeColors.light.paper} label="Surface" />
                      </Box>
                    </Paper>

                    <Paper 
                      sx={{ 
                        p: 2, 
                        border: themeMode === 'dark' ? 2 : 1,
                        borderColor: themeMode === 'dark' ? 'primary.main' : 'divider',
                        bgcolor: themeMode === 'dark' ? 'action.selected' : 'inherit',
                      }}
                    >
                      <FormControlLabel
                        value="dark"
                        control={<Radio disabled={settingsLoading || settingsSaving} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DarkModeIcon />
                            <Typography variant="subtitle1">Dark Theme</Typography>
                          </Box>
                        }
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Easy on the eyes for extended use and low-light environments
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <ColorSwatch color={themeColors.dark.primary} label="Primary" />
                        <ColorSwatch color={themeColors.dark.secondary} label="Secondary" />
                        <ColorSwatch color={themeColors.dark.background} label="Background" />
                        <ColorSwatch color={themeColors.dark.paper} label="Surface" />
                      </Box>
                    </Paper>
                  </RadioGroup>
                </FormControl>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1">Quick Theme Toggle</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Switch between light and dark themes instantly
                    </Typography>
                  </Box>
                  <Switch
                    checked={isDark}
                    onChange={handleThemeToggle}
                    color="primary"
                    disabled={settingsLoading || settingsSaving}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Current Theme Info */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <SettingsIcon color="primary" />
                  <Typography variant="h6">Current Theme Details</Typography>
                </Box>

                <List>
                  <ListItem>
                    <ListItemText
                      primary="Active Theme"
                      secondary="Currently selected theme mode"
                    />
                    <ListItemSecondaryAction>
                      <Chip
                        icon={isDark ? <DarkModeIcon /> : <LightModeIcon />}
                        label={isDark ? 'Dark' : 'Light'}
                        color="primary"
                        variant="outlined"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>

                  <ListItem>
                    <ListItemText
                      primary="Primary Color"
                      secondary="Main brand color used throughout the app"
                    />
                    <ListItemSecondaryAction>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: isDark ? themeColors.dark.primary : themeColors.light.primary,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>

                  <ListItem>
                    <ListItemText
                      primary="Background Color"
                      secondary="Main background color of the application"
                    />
                    <ListItemSecondaryAction>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: isDark ? themeColors.dark.background : themeColors.light.background,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>

                  <ListItem>
                    <ListItemText
                      primary="Paper Color"
                      secondary="Color used for cards and surfaces"
                    />
                    <ListItemSecondaryAction>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: isDark ? themeColors.dark.paper : themeColors.light.paper,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="text.secondary">
                  Theme preferences are automatically saved and will persist across browser sessions.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Thermal Printer Settings */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PrintIcon color="primary" />
                  <Typography variant="h6">Thermal Printer Settings</Typography>
                </Box>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Printer Width</InputLabel>
                  <Select
                    value={thermalWidth}
                    label="Printer Width"
                    onChange={handleThermalWidthChange}
                    disabled={settingsLoading || settingsSaving}
                  >
                    <MenuItem value={58}>58mm (32 characters)</MenuItem>
                    <MenuItem value={80}>80mm (48 characters)</MenuItem>
                  </Select>
                </FormControl>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select the width of your thermal printer. This affects how receipts are formatted when previewed or printed.
                </Typography>

                <List>
                  <ListItem>
                    <ListItemText
                      primary="Current Width"
                      secondary="Selected thermal printer paper width"
                    />
                    <ListItemSecondaryAction>
                      <Chip
                        label={`${thermalWidth}mm`}
                        color="primary"
                        variant="outlined"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>

                  <ListItem>
                    <ListItemText
                      primary="Character Width"
                      secondary="Number of characters per line"
                    />
                    <ListItemSecondaryAction>
                      <Chip
                        label={`${thermalWidth === 58 ? '32' : '48'} chars`}
                        color="secondary"
                        variant="outlined"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1">Order Slip Printing</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enable printing order slips for kitchen/preparation (restaurant-style)
                    </Typography>
                  </Box>
                  <Switch
                    checked={orderSlipEnabled}
                    onChange={handleOrderSlipToggle}
                    color="primary"
                    disabled={settingsLoading || settingsSaving}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="text.secondary">
                  Changes to printer settings are saved automatically and will be applied to all receipt previews.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Advanced Printer Configuration */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PrintIcon color="primary" />
                  <Typography variant="h6">Advanced Printer Configuration</Typography>
                  {settingsSaving && (
                    <CircularProgress size={16} sx={{ ml: 1 }} />
                  )}
                </Box>

                {/* Printer Type Selection */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Printer Type</InputLabel>
                  <Select
                    value={printerType}
                    label="Printer Type"
                    onChange={handlePrinterTypeChange}
                    disabled={settingsLoading || settingsSaving}
                  >
                    <MenuItem value="browser">Browser Print (Default)</MenuItem>
                    <MenuItem value="qz_tray">QZ Tray</MenuItem>
                    <MenuItem value="rawbt">RawBT (Bluetooth)</MenuItem>
                  </Select>
                </FormControl>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Choose your preferred printing method. QZ Tray provides direct thermal printer support, 
                  while RawBT enables Bluetooth printing to compatible devices.
                </Typography>

                {/* QZ Tray Configuration */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle1">QZ Tray Integration</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Direct communication with thermal printers via QZ Tray application
                      </Typography>
                    </Box>
                    <Switch
                      checked={qzTrayEnabled}
                      onChange={handleQzTrayEnabledChange}
                      color="primary"
                      disabled={settingsLoading || settingsSaving}
                    />
                  </Box>

                  {qzTrayEnabled && (
                    <TextField
                      fullWidth
                      label="QZ Tray Printer Name"
                      placeholder="e.g., EPSON TM-T82, Star TSP143"
                      value={qzTrayPrinterName}
                      onChange={handleQzTrayPrinterNameChange}
                      disabled={settingsLoading || settingsSaving}
                      sx={{ mt: 2 }}
                      helperText="Enter the exact name of your thermal printer as it appears in QZ Tray"
                    />
                  )}
                </Box>

                {/* RawBT Configuration */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle1">RawBT Bluetooth Printing</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Print to Bluetooth-enabled thermal printers
                      </Typography>
                    </Box>
                    <Switch
                      checked={rawbtEnabled}
                      onChange={handleRawbtEnabledChange}
                      color="primary"
                      disabled={settingsLoading || settingsSaving}
                    />
                  </Box>

                </Box>

                <Divider sx={{ my: 2 }} />

                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Setup Instructions:</strong><br />
                    • QZ Tray: Download and install QZ Tray from qz.io, ensure it's running<br />
                    • RawBT: Uses RawBT URI scheme for Bluetooth printing (Chrome recommended)<br />
                    • Always test your printer configuration before using in production
                  </Typography>
                </Alert>

                <Typography variant="body2" color="text.secondary">
                  Advanced printer configurations require additional setup. Refer to documentation for detailed instructions.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Service Charge Settings */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <MoneyIcon color="primary" />
                  <Typography variant="h6">Service Charge Settings</Typography>
                  {settingsSaving && (
                    <CircularProgress size={16} sx={{ ml: 1 }} />
                  )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box>
                    <Typography variant="subtitle1">Enable Service Charge</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Automatically add service charge to transactions
                    </Typography>
                  </Box>
                  <Switch
                    checked={serviceChargeEnabled}
                    onChange={handleServiceChargeEnabledChange}
                    color="primary"
                    disabled={settingsSaving || settingsLoading}
                  />
                </Box>

                <TextField
                  label="Service Charge Percentage"
                  type="number"
                  value={serviceChargePercentage}
                  onChange={handleServiceChargePercentageChange}
                  disabled={!serviceChargeEnabled || settingsSaving || settingsLoading}
                  fullWidth
                  inputProps={{ 
                    min: 0, 
                    max: 100, 
                    step: 0.01 
                  }}
                  helperText={
                    serviceChargeEnabled 
                      ? `${serviceChargePercentage}% will be ${serviceChargeInclusive ? 'included in' : 'added to'} the total amount` 
                      : 'Enable service charge to set percentage'
                  }
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box>
                    <Typography variant="subtitle1">Service Charge Mode</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {serviceChargeInclusive 
                        ? 'Inclusive: Service charge is included in the item prices' 
                        : 'Exclusive: Service charge is added to the subtotal'}
                    </Typography>
                  </Box>
                  <Switch
                    checked={serviceChargeInclusive}
                    onChange={handleServiceChargeInclusiveChange}
                    color="primary"
                    disabled={!serviceChargeEnabled || settingsSaving || settingsLoading}
                  />
                </Box>

                <List>
                  <ListItem>
                    <ListItemText
                      primary="Current Status"
                      secondary="Service charge configuration status"
                    />
                    <ListItemSecondaryAction>
                      <Chip
                        label={serviceChargeEnabled ? 'Enabled' : 'Disabled'}
                        color={serviceChargeEnabled ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>

                  {serviceChargeEnabled && (
                    <>
                      <ListItem>
                        <ListItemText
                          primary="Service Charge Rate"
                          secondary="Percentage added to transactions"
                        />
                        <ListItemSecondaryAction>
                          <Chip
                            label={`${serviceChargePercentage}%`}
                            color="primary"
                            variant="outlined"
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                      
                      <ListItem>
                        <ListItemText
                          primary="Service Charge Mode"
                          secondary={serviceChargeInclusive ? "Included in item prices" : "Added to subtotal"}
                        />
                        <ListItemSecondaryAction>
                          <Chip
                            label={serviceChargeInclusive ? 'Inclusive' : 'Exclusive'}
                            color={serviceChargeInclusive ? 'info' : 'warning'}
                            variant="outlined"
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    </>
                  )}
                </List>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="text.secondary">
                  Service charge settings are automatically saved and will apply to all new transactions.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Additional Settings */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Additional Settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  More settings options will be available here in future updates, including:
                </Typography>
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Language preferences
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Notification settings
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Receipt templates
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Data export options
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    User account management
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Send Feedback */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Send Feedback
                </Typography>
                <Box
                  component="form"
                  noValidate
                  autoComplete="off"
                  sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                  <TextField
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    fullWidth
                    multiline
                    rows={4}
                    required
                  />
                  <Button variant="contained" color="primary" onClick={handleSendFeedback}>
                    Send Feedback
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Settings;
